import React, { useEffect, useState } from 'react';
import { Search, Download, AlertCircle, Loader } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../services/apiClient.ts';
import DateInput from '../components/common/DateInput';

const MissingRecipients = () => {
  const { isArabic } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState(null);

  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [branchId, setBranchId] = useState('');

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Fetch missing recipients
  const handleSearch = async (e) => {
    e.preventDefault();
    console.log('Initiating search with filters:', { fromDate, toDate, branchId });
    
    console.log('Searching with:', { fromDate, toDate, branchId });
    
    if (!fromDate || !toDate) {
      setToast({ type: 'error', message: 'يجب تحديد نطاق التاريخ' });
      return;
    }
    
     // setLoading(true);
     setError(null);
     setResult(null);
  
    try {
      
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
      });

      console.log('Request Params:', params);
      if (branchId) params.append('branch_id', branchId);

      const response = await apiClient.get(`/v1/reports/messages/missing-recipients?${params}`);
      console.log('API Response:', response);
      setResult(response.data);
      setToast({ type: 'success', message: 'تم جلب البيانات بنجاح' });
    } catch (err) {
      console.error('API Error:', err);
      setError(err.response?.data?.message || 'خطأ في جلب البيانات');
      setToast({ type: 'error', message: 'فشل في جلب البيانات' });
    } finally {
      setLoading(false);
    }
    
  };
  // Export to CSV
  const exportToCSV = () => {
    if (!result || result.missing_travelers.length === 0) {
      setToast({ type: 'error', message: 'لا توجد بيانات للتصدير' });
      return;
    }

    const headers = ['اسم المسافر', 'الهاتف 1', 'الهاتف 2', 'جواز السفر', 'الفرع'];
    const rows = result.missing_travelers.map(traveler => [
      traveler.name,
      traveler.phone1 || '-',
      traveler.phone2 || '-',
      traveler.passport || '-',
      traveler.branch_id,
    ]);

    let csvContent = headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `المستقبلون_الذين_لم_يتلقوا_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    setToast({ type: 'success', message: 'تم تصدير البيانات بنجاح' });
  };

  const renderContent = () => {
    console.log('Rendering content with state:', { loading, error, result });
    if (loading) {
      console.log('Loading data...');
      return (
        <div className="bg-white rounded-lg shadow p-12 flex justify-center items-center">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      );
    }

    if (error) {
      console.error('Error encountered:', error);
      return (
        <div className="bg-white rounded-lg shadow p-6 flex items-center gap-3 bg-red-50">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">خطأ</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      );
    }

    if (result) {
      console.log('Rendering results:', result);
      return (
        <div className="bg-white rounded-lg shadow p-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">إجمالي المسافرين</div>
              <div className="text-2xl font-bold text-blue-600">{result.total_travelers}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">تلقوا الرسائل</div>
              <div className="text-2xl font-bold text-green-600">{result.travelers_received_messages}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">لم يتلقوا الرسائل</div>
              <div className="text-2xl font-bold text-red-600">{result.missing_count}</div>
            </div>
          </div>

          {/* Missing Recipients Table */}
          {result.missing_count > 0 ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">المسافرون الذين لم يتلقوا الرسائل</h2>
                <button
                  onClick={exportToCSV}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  <Download className="w-4 h-4" />
                  تصدير CSV
                </button>
              </div>

              <div className="app-table-wrap">
                <table className="app-table w-full">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">الهاتف 2</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">الهاتف 1</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">جواز السفر</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">الاسم</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {result.missing_travelers.map((traveler, idx) => (
                      <tr key={`traveler-${idx}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-600 font-mono">{traveler.phone1 || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 font-mono">{traveler.phone2 || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{traveler.passport || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{traveler.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-lg">الجميع تلقوا الرسائل بنجاح! ✓</p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
        <Search className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-lg">استخدم النموذج أعلاه للبحث عن المستقبلين الذين لم يتلقوا رسائل</p>
      </div>
    );
  };

  return (
    <div className={`min-h-screen bg-gray-50 p-6 ${isArabic ? 'rtl' : 'ltr'}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">المستقبلون الذين لم يتلقوا الرسائل</h1>
          <p className="text-gray-600">ابحث عن المسافرين الذين لم يتلقوا رسائل تذكيرية في فترة زمنية محددة</p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-lg shadow p-6 mb-6"> 
          <form onSubmit={(e) => {setLoading(true);handleSearch(e);}}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* To Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">إلى</label>
                <DateInput

 value={toDate}
 onChange={(e) => setToDate(e.target.value)}
 required
 className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
 />
              </div>

              {/* From Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">من</label>
                <DateInput

 value={fromDate}
 onChange={(e) => setFromDate(e.target.value)}
 required
 className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
 />
              </div>


              {/* Branch Filter (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الفرع (اختياري)</label>
                <input
                  type="number"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  placeholder="معرف الفرع..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-start gap-2">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading && <Loader className="w-4 h-4 animate-spin" />}
                بحث
              </button>
            </div>
          </form>
        </div>

        {/* Content Area - Single Rendering Point */}
        
        {console.log('About to render content...')}
        {renderContent()}
      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed bottom-4 ${isArabic ? 'right-4' : 'left-4'} max-w-sm p-4 rounded-lg text-white text-sm shadow-lg transition-opacity ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default MissingRecipients;
